import { useState, useEffect } from "react";
import { HexColorPicker } from "react-colorful";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, RotateCcw } from "lucide-react";

export function ColorPickerTool() {
  const [color, setColor] = useState("#3b82f6");
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyToClipboard(color).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Color Picker Online - Visual Hex Color Wheel Selector"
      description="Easily pick, adjust, and find color codes with our interactive browser-based color wheel. Export hex, rgb, or hsl color codes instantly."
      h1="Color Picker"
      category="Color Tools"
      categoryPath="/color-picker"
      howToUse={[
        "Click or drag on the interactive color palette to choose a hue.",
        "Inspect the live color preview box immediately.",
        "Copy your final Hex color string directly to your clipboard."
      ]}
      faq={[
        { question: "What code format is generated?", answer: "It generates standard hexadecimal (e.g., #3b82f6) css-compliant hex code strings." },
        { question: "Does this require graphic software?", answer: "No, this color picker operates completely inside your web browser for fast developer workflows." }
      ]}
    >
      <div className="flex flex-col md:flex-row gap-8 items-center justify-center py-8 max-w-2xl mx-auto bg-white border border-slate-200 rounded-2xl p-6 shadow-xs">
        <HexColorPicker color={color} onChange={setColor} />
        <div className="space-y-4 flex flex-col items-center">
          <div className="w-32 h-32 rounded-2xl shadow-inner border border-slate-200 transition-colors duration-200" style={{ backgroundColor: color }} />
          <div className="space-y-2 w-full">
            <Input value={color} onChange={e => setColor(e.target.value)} className="font-mono text-center uppercase tracking-wider font-bold text-slate-800" />
            <Button onClick={handleCopy} className="w-full bg-slate-900 hover:bg-slate-800 text-white gap-1.5 h-10 text-xs">
              <Copy className="w-4 h-4" />
              {copied ? "Copied!" : "Copy Hex Code"}
            </Button>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function HslConverter() {
  const [h, setH] = useState(217);
  const [s, setS] = useState(91);
  const [l, setL] = useState(60);
  const [copied, setCopied] = useState(false);

  const hsl = `hsl(${h}, ${s}%, ${l}%)`;

  const handleCopy = () => {
    copyToClipboard(hsl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="HSL Converter Online - Color Conversion Sliders"
      description="Adjust and translate HSL (Hue, Saturation, Lightness) properties using precise visual sliders. Export CSS-ready hsl() color styles instantly."
      h1="HSL Converter"
      category="Color Tools"
      categoryPath="/color-picker"
      howToUse={[
        "Drag the Hue range slider (0-360 degrees) to choose primary pigments.",
        "Slide Saturation (0-100%) to control intensity, and Lightness (0-100%) for brightness.",
        "Copy your final styled hsl() string directly."
      ]}
      faq={[
        { question: "What is HSL?", answer: "HSL stands for Hue, Saturation, and Lightness, offering an intuitive coordinate mapping of pigments." },
        { question: "Can HSL strings be parsed directly by browsers?", answer: "Yes. All modern HTML engines and browsers render CSS hsl() styles natively." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-8 items-center max-w-3xl mx-auto bg-white border border-slate-200 rounded-2xl p-6 shadow-xs">
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between text-xs font-semibold text-slate-500 uppercase">
              <Label>Hue</Label>
              <span>{h}°</span>
            </div>
            <Input type="range" min="0" max="360" value={h} onChange={e => setH(Number(e.target.value))} className="h-5 cursor-pointer" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs font-semibold text-slate-500 uppercase">
              <Label>Saturation</Label>
              <span>{s}%</span>
            </div>
            <Input type="range" min="0" max="100" value={s} onChange={e => setS(Number(e.target.value))} className="h-5 cursor-pointer" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs font-semibold text-slate-500 uppercase">
              <Label>Lightness</Label>
              <span>{l}%</span>
            </div>
            <Input type="range" min="0" max="100" value={l} onChange={e => setL(Number(e.target.value))} className="h-5 cursor-pointer" />
          </div>
          <div className="pt-4 border-t border-slate-100 space-y-2">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-semibold text-slate-700">HSL Output String</Label>
              <Button variant="outline" size="sm" onClick={handleCopy} className="text-xs gap-1 py-1 h-7">
                <Copy className="w-3.5 h-3.5" />
                {copied ? "Copied!" : "Copy CSS"}
              </Button>
            </div>
            <Input value={hsl} readOnly className="font-mono text-center uppercase text-base h-11 bg-slate-50 font-bold text-slate-800" />
          </div>
        </div>
        <div 
          className="w-full h-full min-h-[250px] rounded-2xl shadow-inner border border-slate-200 transition-colors duration-150"
          style={{ backgroundColor: hsl }} 
        />
      </div>
    </ToolPageLayout>
  );
}

export function ColorPaletteGenerator() {
  const [base, setBase] = useState("#3b82f6");
  const [shades, setShades] = useState<string[]>([]);

  const generateShades = (hex: string) => {
    // Generate shades using css color-mix approximations or standard lightness increments
    const counts = [10, 20, 30, 40, 50, 60, 70, 80, 90];
    setShades(counts.map(pct => `color-mix(in srgb, ${hex} ${pct}%, white)`));
  };

  useEffect(() => {
    generateShades(base);
  }, [base]);

  return (
    <ToolPageLayout
      title="Color Palette Generator - Create Beautiful Shading Schemes"
      description="Generate gorgeous, consistent shading schemes, gradients, and monochromatic color palettes from a single base Hex color code instantly."
      h1="Color Palette Generator"
      category="Color Tools"
      categoryPath="/color-picker"
      howToUse={[
        "Type your base Hex color code or click the picker icon to choose visually.",
        "See a list of matching shades generated from 10% to 90% opacity mixed with white.",
        "Easily grab individual codes for your mockups or styling panels."
      ]}
      faq={[
        { question: "What is a monochromatic palette?", answer: "It is a color scheme built of multiple shades, tints, and tones of a single parent base color." },
        { question: "Does this generator support tailwind colors?", answer: "Yes, you can easily use these color ratios to define custom Tailwind theme shade sets (e.g., 100 to 900)." }
      ]}
    >
      <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div className="flex gap-3 items-center">
            <Label className="text-sm font-semibold text-slate-700">Base Color</Label>
            <Input value={base} onChange={e => setBase(e.target.value)} className="w-32 font-mono uppercase text-center font-bold text-slate-800" />
            <input type="color" value={base} onChange={e => setBase(e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-0.5" />
          </div>
          <Button variant="outline" size="sm" onClick={() => { setBase("#3b82f6"); }} className="text-xs gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" />
            Reset Base
          </Button>
        </div>
        
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
          {shades.map((shade, i) => {
            const level = (i + 1) * 10;
            return (
              <div key={i} className="flex flex-col items-center gap-2">
                <div 
                  className="w-full aspect-square rounded-xl shadow-xs border border-slate-100" 
                  style={{ backgroundColor: shade }} 
                />
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Level {level}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </ToolPageLayout>
  );
}

// WCAG Contrast calculation functions
function getRGB(c: string) {
  let hex = c.replace('#', '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return { r, g, b };
}

function getLuminance(r: number, g: number, b: number) {
  const a = [r, g, b].map(v => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

function getContrastRatio(fgStr: string, bgStr: string) {
  try {
    const fgRGB = getRGB(fgStr);
    const bgRGB = getRGB(bgStr);
    const fgL = getLuminance(fgRGB.r, fgRGB.g, fgRGB.b);
    const bgL = getLuminance(bgRGB.r, bgRGB.g, bgRGB.b);
    const l1 = Math.max(fgL, bgL);
    const l2 = Math.min(fgL, bgL);
    return (l1 + 0.05) / (l2 + 0.05);
  } catch (e) {
    return 1;
  }
}

export function ContrastChecker() {
  const [fg, setFg] = useState("#0f172a");
  const [bg, setBg] = useState("#f8fafc");

  const ratio = getContrastRatio(fg, bg);
  const ratioStr = ratio.toFixed(2);
  const passAA_Normal = ratio >= 4.5;
  const passAA_Large = ratio >= 3.0;
  const passAAA_Normal = ratio >= 7.0;
  const passAAA_Large = ratio >= 4.5;

  return (
    <ToolPageLayout
      title="Contrast Checker Online - WCAG 2.1 Color Accessibility"
      description="Check color contrast ratios against WCAG 2.1 criteria. Verify AA and AAA accessibility levels for text readability and modern web components."
      h1="Contrast Checker"
      category="Color Tools"
      categoryPath="/color-picker"
      howToUse={[
        "Type or select your Foreground (Text) color.",
        "Type or select your Background color.",
        "Review WCAG 2.1 compliance scores (AA/AAA) and interactive previews instantly."
      ]}
      faq={[
        { question: "What contrast ratio is required for WCAG AA compliance?", answer: "A contrast ratio of at least 4.5:1 is required for normal-sized text, and 3:1 for large text (over 18pt or bold over 14pt)." },
        { question: "What is AAA contrast compliance?", answer: "A stricter standard requiring a minimum contrast ratio of 7:1 for standard text and 4.5:1 for large text." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-stretch">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Foreground (Text Color)</Label>
              <div className="flex gap-2 mt-1.5">
                <Input value={fg} onChange={e => setFg(e.target.value)} className="font-mono text-sm" />
                <input type="color" value={fg} onChange={e => setFg(e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border p-0.5" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Background Color</Label>
              <div className="flex gap-2 mt-1.5">
                <Input value={bg} onChange={e => setBg(e.target.value)} className="font-mono text-sm" />
                <input type="color" value={bg} onChange={e => setBg(e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border p-0.5" />
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-slate-700">Contrast Ratio</span>
              <span className="text-2xl font-black text-slate-900">{ratioStr}:1</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className={`p-3 rounded-xl border text-center ${passAA_Normal ? "bg-emerald-50 text-emerald-800 border-emerald-100" : "bg-red-50 text-red-800 border-red-100"}`}>
                <div className="text-xs font-bold uppercase tracking-wider">AA Normal Text</div>
                <div className="text-sm font-black mt-1">{passAA_Normal ? "PASS" : "FAIL"}</div>
              </div>
              <div className={`p-3 rounded-xl border text-center ${passAA_Large ? "bg-emerald-50 text-emerald-800 border-emerald-100" : "bg-red-50 text-red-800 border-red-100"}`}>
                <div className="text-xs font-bold uppercase tracking-wider">AA Large Text</div>
                <div className="text-sm font-black mt-1">{passAA_Large ? "PASS" : "FAIL"}</div>
              </div>
              <div className={`p-3 rounded-xl border text-center ${passAAA_Normal ? "bg-emerald-50 text-emerald-800 border-emerald-100" : "bg-red-50 text-red-800 border-red-100"}`}>
                <div className="text-xs font-bold uppercase tracking-wider">AAA Normal Text</div>
                <div className="text-sm font-black mt-1">{passAAA_Normal ? "PASS" : "FAIL"}</div>
              </div>
              <div className={`p-3 rounded-xl border text-center ${passAAA_Large ? "bg-emerald-50 text-emerald-800 border-emerald-100" : "bg-red-50 text-red-800 border-red-100"}`}>
                <div className="text-xs font-bold uppercase tracking-wider">AAA Large Text</div>
                <div className="text-sm font-black mt-1">{passAAA_Large ? "PASS" : "FAIL"}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-8 rounded-2xl border flex flex-col items-center justify-center text-center shadow-xs min-h-[300px] transition-all duration-200" style={{ backgroundColor: bg, color: fg }}>
          <h3 className="text-4xl font-black tracking-tight mb-2">Typography Preview</h3>
          <p className="text-sm font-medium opacity-85 max-w-sm">This is how your selected text foreground color renders on top of the background canvas.</p>
          <span className="text-xs font-mono mt-8 opacity-60">Contrast Ratio: {ratioStr}:1</span>
        </div>
      </div>
    </ToolPageLayout>
  );
}
